import React from 'react'
import { Div, Flex, Separator, Text } from '@sendoutcards/quantum-design-ui'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { IconType } from 'src/design_system/atoms/icons/types'

export type TotalsTableProps = {
  subtotal: string
  giftCardTotal?: string
  processingFeesTotal?: string
  taxTotal?: string
  orderTotal: string
  discounts?: {
    name: string
    sku: string
    amount: string
  }[]
  recurringAmount?: { amount: string; occurrence: string }
  upgrades?: {
    name: string
    sku: string
    amount: string
  }
  shouldShowBoxShadow?: boolean
}

type PricingLineProps = {
  title: string
  price?: string
  icon?: { name: IconType; color?: string; size?: number }
  discounts?: {
    name: string
    amount: string
  }
  shouldRemovePadding?: boolean
  fontSettings?: {
    size?: 'body' | 'footnote' | 'caption'
    color?: string
  }
  overridePadding?: string
}
const PricingLine: React.FC<PricingLineProps> = ({
  title,
  price,
  icon,
  shouldRemovePadding,
  fontSettings,
  overridePadding,
}) => {
  return (
    <Flex
      width="100%"
      inset={!shouldRemovePadding ? { vertical: 'x1' } : undefined}
      alignItems={'center'}
      columnGap="x_5"
      style={overridePadding ? { padding: overridePadding } : {}}
    >
      {icon && <Icon name={icon.name} size={icon.size} color={icon.color} />}
      <Div>
        <Text
          content={title}
          type={fontSettings?.size ?? 'body'}
          color={'primaryHeading'}
          weight="bold"
          style={{ color: fontSettings?.color }}
        />
      </Div>
      {price && (
        <Div style={{ marginLeft: 'auto' }}>
          <Text
            content={price}
            type={fontSettings?.size ?? 'body'}
            color="primaryHeading"
            weight="bold"
            style={{ color: fontSettings?.color }}
          />
        </Div>
      )}
    </Flex>
  )
}

export const TotalsTable = (props: TotalsTableProps) => {
  const {
    subtotal,
    discounts,
    taxTotal,
    orderTotal,
    recurringAmount,
    upgrades,
    giftCardTotal,
    processingFeesTotal,
    shouldShowBoxShadow,
  } = props

  const shouldDisplaySubtotal = orderTotal !== '0.00'
  return (
    <Flex
      backgroundColor="foreground"
      flexDirection="column"
      width="100%"
      borderRadius="large"
      inset={{ vertical: 'x1', horizontal: 'x1' }}
      boxShadow={shouldShowBoxShadow ? 'mediumLight' : undefined}
    >
      {shouldDisplaySubtotal && (
        <Flex
          flexDirection="column"
          backgroundColor="background"
          inset="x2"
          style={{ borderRadius: '20px', marginBottom: '1rem' }}
        >
          <PricingLine title={'Subtotal'} price={`$${subtotal}`} />
          {discounts && discounts.length > 0 && (
            <>
              <PricingLine
                title={'Discounts & Savings'}
                shouldRemovePadding={false}
              />
              {discounts.map(discount => (
                <PricingLine
                  overridePadding="0 0 0 12px"
                  icon={{ name: 'premium_crown', size: 16, color: '#FECD39' }}
                  title={
                    discount.sku === 'ActToken' ? '1 Act Token' : discount.name
                  }
                  price={`-$${discount.amount}`}
                  fontSettings={{
                    size: 'footnote',
                    color: 'gray',
                  }}
                />
              ))}
            </>
          )}
          {upgrades && (
            <>
              <PricingLine title={'Upgrades'} shouldRemovePadding={false} />
              <PricingLine
                overridePadding="0 0 0 12px"
                icon={{ name: 'premium_crown', size: 16, color: '#FECD39' }}
                title={
                  upgrades.sku === 'PremiumCard'
                    ? 'Premium Card Design'
                    : upgrades.name
                }
                price={`$${upgrades.amount}`}
                fontSettings={{
                  size: 'footnote',
                  color: 'gray',
                }}
              />
            </>
          )}
          {giftCardTotal && (
            <PricingLine title={'Gift Card'} price={`$${giftCardTotal}`} />
          )}
          {processingFeesTotal && (
            <PricingLine
              title={'Processing Fees'}
              price={`$${processingFeesTotal}`}
            />
          )}
          {taxTotal && <PricingLine title={'Taxes'} price={`$${taxTotal}`} />}
        </Flex>
      )}
      <Flex
        flexDirection="column"
        style={{ padding: `0 1rem ${shouldDisplaySubtotal ? '5px' : ''}` }}
      >
        <PricingLine title={'Total Due Today'} price={`$${orderTotal}`} />
        {recurringAmount && (
          <>
            <Div inset={{ horizontal: 'x1' }} outset={{ vertical: 'x1' }}>
              <Separator orientation="horizontal" />
            </Div>
            <PricingLine
              title="Total Ongoing"
              price={`$${recurringAmount.amount}/${recurringAmount.occurrence}`}
            />
          </>
        )}
      </Flex>
    </Flex>
  )
}
